// @flow
import { changePaginationPageSize, getSearchPageSize } from '../../redux/modules/pagination';
import {
    deleteSavedSearch,
    getSaveSearchParams,
    getSaveSearchState,
    submitSaveSearch,
} from '../../redux/modules/saveSearch';
import {
    fetchSearchFacetsIfNeeded,
    getFetchFacetsSubmitted,
    getRawSearchFacets,
} from '../../redux/modules/searchFacets';
import {
    getPageFromQueryParams,
    isArchivedSearch,
    modifyParamsRemoveAllExceptCategories,
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemoveDomesticFlatShipping,
    modifyParamsRemoveFacet,
    modifyParamsRemovePage,
    modifyParamsRemovePublishDate,
    modifyParamsRemoveSaveSearch,
    modifyParamsReplaceCountry,
    modifyParamsWhenChecked,
    modifyParamsWithDistance,
    modifyParamsWithDomesticFlatShipping,
    modifyParamsWithPage,
    modifyParamsWithPageSize,
    modifyParamsWithRange,
    modifyParamsWithSearchStatus,
    modifyParamsWithSort,
    modifyParamsWithTaxonomyAll,
    modifyParamsWithTaxonomyParent,
    modifyParamsWithTaxonomySelected,
    parseQueryParams,
} from '../../utils/queryParams';
import { getPromotedItems, getPromotedSearchResults, searchPromoted } from '../../redux/modules/searchPromoted';
import { getSaveSearchCheckState, submitIsSavedSearchCheck } from '../../redux/modules/saveSearchCheck';
import {
    getSearchedItemIds,
    getSearchedItems,
    getSearchFacets,
    getSearchUiState,
    submitSearch,
} from '../../redux/modules/search';
import { getUserExcludedFacets, setSearchExclusion } from '../../redux/modules/searchExclusions';
import { getWhiteLabelSellerId, isWhiteLabel } from '../../redux/modules/whiteLabel';
import React, { Component } from 'react';
import { recordAnalyticsForSearchItemsPageView, trackClickForAlgolia } from '../../redux/modules/analytics';
import { bindActionCreators } from 'redux';
import { buildSearchQuery } from '../../utils/searchQuery';
import { connect } from 'react-redux';
import { DATA_LOADING_ID } from './helpers/searchPageData';
import type { ExtractReturn } from '../../types/redux';
import { fetchItemsIfNeeded } from '../../redux/modules/item';
import { getAuthToken } from '../../redux/modules/user';
import { getBaseUrl } from '../../redux/modules/config';
import { getSearchUrlWithParams } from '../../utils/urls';
import { getSecondarySearchSuggestions } from '../../redux/modules/searchSuggestions';
import { getSellerCatalogCounts } from '../../redux/modules/sellerCatalogCounts';
import { getWindowSize } from '../../redux/modules/windowSize';
import type { GlobalState } from '../../redux/rootReducer';
import isEqual from 'react-fast-compare';
import { isServerSideRendered } from '../../redux/modules/route';
import { push as navigate } from 'connected-react-router';
import { openLoginModal } from '../../redux/modules/modal';
import SearchPage from './SearchPage';
import { setHeaderDropdownVisible } from '../../redux/modules/headerSearchDropdown';
import { toast } from 'react-toastify';
import withRouteDataLoader from '../../client/helpers/withRouteDataLoader';

type OwnProps = $ReadOnly<{|
    location: any,
    routeIsLoading: boolean,
|}>;

const mapStateToProps = (state: GlobalState, { location }: OwnProps) => {
    const { pathname } = location;
    const queryParams = parseQueryParams(location.search);
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const authToken = getAuthToken(state);
    const baseUrl = getBaseUrl(state);
    const excludedFacets = getUserExcludedFacets(state);
    const facets = getSearchFacets(state, archivedSearch);
    const itemIds = getSearchedItemIds(state);
    const items = getSearchedItems(state);
    const page = getPageFromQueryParams(queryParams);
    const pageSize = getSearchPageSize(state);
    const promotedItemIds = getPromotedSearchResults(state);
    const promotedItems = getPromotedItems(state);
    const rawFacets = getRawSearchFacets(state, archivedSearch);
    const saveSearchCheckState = getSaveSearchCheckState(state);
    const saveSearchState = getSaveSearchState(state);
    const searchQuery = buildSearchQuery({ pathname, queryParams, rawFacets, searchPageSize: pageSize });
    const ssr = isServerSideRendered(state);
    const whiteLabel = isWhiteLabel(state);
    const { width } = getWindowSize(state);
    const { houses: potentialHouses } = getSecondarySearchSuggestions(state);
    let houses = [];
    if (potentialHouses.length) {
        const { doneCount, upcomingCount } = getSellerCatalogCounts(state, potentialHouses[0].houseId);

        // we wont show new houses unless they have multiple upcoming auctions until they have finished at least one
        if ((doneCount === 0 && upcomingCount > 1) || (doneCount > 0 && upcomingCount >= 1)) {
            houses = potentialHouses;
        }
    }

    const {
        error: searchFailed,
        isLoading,
        sort: sortOptions,
        submitted,
        totalFound,
        totalLiveItems,
        totalPages,
        totalSoldItems,
    } = getSearchUiState(state);

    const pageName = searchQuery.options.status.includes('sold') ? 'sold-search' : 'search';
    const searchSubmitted = submitted || getFetchFacetsSubmitted(state);

    const sellerId = getWhiteLabelSellerId(state);

    return {
        archivedSearch,
        authToken,
        baseUrl,
        excludedFacets,
        facets,
        houses,
        isLoading,
        itemIds,
        items,
        location,
        page,
        pageName,
        pageSize,
        pathname,
        promotedItemIds,
        promotedItems,
        queryParams,
        rawFacets,
        saveSearchCheckState,
        saveSearchState,
        searchFailed,
        searchQuery,
        searchSubmitted,
        sellerId,
        sortOptions,
        ssr,
        totalFound,
        totalLiveItems,
        totalPages,
        totalSoldItems,
        whiteLabel,
        width,
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changePaginationPageSize,
            deleteSavedSearch,
            fetchItemsIfNeeded,
            fetchSearchFacetsIfNeeded,
            navigate,
            openLoginModal,
            recordAnalyticsForSearchItemsPageView,
            searchPromoted,
            setHeaderDropdownVisible,
            setSearchExclusion,
            submitIsSavedSearchCheck,
            submitSaveSearch,
            submitSearch,
            trackClickForAlgolia,
        },
        dispatch
    );

type ReduxProps = $ReadOnly<{|
    ...ExtractReturn<typeof mapStateToProps>,
    archivedSearch: boolean,
    authToken: string,
    baseUrl: string,
    excludedFacets: any,
    facets: any[],
    houses: any[],
    isLoading: boolean,
    itemIds: any[],
    items: any[],
    location: any,
    page: number,
    pageName: string,
    pageSize: number,
    pathname: string,
    promotedItemIds: any[],
    promotedItems: any[],
    queryParams: any,
    rawFacets: any[],
    saveSearchCheckState: any,
    saveSearchState: any,
    searchFailed: boolean,
    searchQuery: any,
    searchSubmitted: boolean,
    sellerId: number,
    sortOptions: any[],
    ssr: boolean,
    totalFound: number,
    totalLiveItems: number,
    totalPages: number,
    totalSoldItems: number,
    whiteLabel: boolean,
    width: number,
|}>;

type ReduxActions = $ReadOnly<{|
    changePaginationPageSize: typeof changePaginationPageSize,
    deleteSavedSearch: typeof deleteSavedSearch,
    fetchItemsIfNeeded: typeof fetchItemsIfNeeded,
    navigate: typeof navigate,
    openLoginModal: typeof openLoginModal,
    recordAnalyticsForSearchItemsPageView: typeof recordAnalyticsForSearchItemsPageView,
    searchPromoted: typeof searchPromoted,
    setHeaderDropdownVisible: typeof setHeaderDropdownVisible,
    setSearchExclusion: typeof setSearchExclusion,
    submitIsSavedSearchCheck: typeof submitIsSavedSearchCheck,
    submitSaveSearch: typeof submitSaveSearch,
    submitSearch: typeof submitSearch,
    trackClickForAlgolia: typeof trackClickForAlgolia,
|}>;

type Props = $ReadOnly<{|
    ...OwnProps,
    ...ReduxProps,
    ...ReduxActions,
|}>;

class SearchPageContainer extends Component<Props> {
    componentDidMount() {
        if (this.props.ssr) {
            // record analytics the on page load for SSR pages
            this.props.recordAnalyticsForSearchItemsPageView();
        }
    }

    componentDidUpdate(prevProps) {
        // Don't update redux while the initial page data is loading
        if (this.props.routeIsLoading) return;

        // Fetch the items that the search service needs
        if (
            !isEqual(prevProps.itemIds, this.props.itemIds) ||
            !isEqual(prevProps.promotedItemIds, this.props.promotedItemIds)
        ) {
            this.props.fetchItemsIfNeeded([...this.props.itemIds, ...this.props.promotedItemIds], DATA_LOADING_ID);
        }
        // Perform search if excluded facets change
        if (!isEqual(prevProps.excludedFacets, this.props.excludedFacets)) {
            const { searchQuery } = this.props;
            const pageName = searchQuery.options.status.includes('sold') ? 'sold-search' : 'search';
            this.props.submitSearch(searchQuery);
            this.props.searchPromoted({ pageName, searchQuery });
        }

        // Check if search is saved since auth token will be hydrated in after component has mounted
        if (!prevProps.authToken && this.props.authToken) {
            const ssParams = getSaveSearchParams(this.props.queryParams);
            this.props.submitIsSavedSearchCheck(ssParams);
        }

        // If the url has the saveSearch param, save the search and redirect
        if (
            !prevProps.authToken &&
            this.props.authToken &&
            this.props.queryParams.saveSearch &&
            !this.props.saveSearchCheckState.isSavedSearch
        ) {
            this.submitSaveSearch();
            let newParams = modifyParamsRemoveSaveSearch(this.props.queryParams);
            this.navigateWithParams(newParams);

            toast('Search Saved!');
        }

        // If the url has the saveSearch param but we are not logged in, open the log in modal
        if (!prevProps.authToken && !this.props.authToken && this.props.queryParams.saveSearch) {
            this.props.openLoginModal();
        }

        // Because component did update gets call multiple times after a navigate call is made, we cant rely completly
        // on the "page" property to decide whether to call the navigate or not.  Because of this we have a boolean
        // that we set on page load that will only be true the first time and then once set wont be true again.
        // this will allow us to redirect properly to the first page in cases where a bidder has a page in the query
        // that doesnt contain results
        if (
            !this.props.archivedSearch &&
            this.props.page > 1 &&
            Math.ceil(this.props.totalLiveItems / this.props.pageSize) < this.props.page &&
            !this.navigating
        ) {
            const newParams = modifyParamsWithPage(this.props.queryParams, 1);
            this.navigateWithParams(newParams);
            this.navigating = true;
        }

        if (
            this.props.archivedSearch &&
            this.props.page > 1 &&
            Math.ceil(this.props.totalSoldItems / this.props.pageSize) < this.props.page &&
            !this.navigating
        ) {
            const newParams = modifyParamsWithPage(this.props.queryParams, 1);
            this.navigateWithParams(newParams);
            this.navigating = true;
        }
    }

    componentWillUnmount() {
        this.props.setHeaderDropdownVisible(false);
    }

    navigating: boolean;

    changePageAndNavigate = selected => {
        const page = selected + 1;
        const newParams = modifyParamsWithPage(this.props.queryParams, page);
        this.navigateWithParams(newParams);
    };

    changePageSizeAndNavigate = pageSize => {
        this.props.changePaginationPageSize(pageSize);
        const newParams = modifyParamsWithPageSize(this.props.queryParams, pageSize);
        this.navigateWithParams(newParams);
    };

    changeSearchStatus = mode => {
        let newParams = modifyParamsWithSearchStatus(this.props.queryParams, mode, this.props.sellerId);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
        this.props.setHeaderDropdownVisible(true);
    };

    clearAllParamsExceptCategories = () => {
        let newParams = modifyParamsRemoveAllExceptCategories(this.props.queryParams, this.props.facets);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    deleteSavedSearch = () => {
        const ssParams = getSaveSearchParams(this.props.queryParams);
        this.props.deleteSavedSearch(ssParams);
    };

    distanceSelected = (country, zipCode, range, measurement) => {
        let newParams = modifyParamsWithDistance(this.props.queryParams, country, zipCode, range, measurement);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    domesticFlatShippingChecked = (value, checked) => {
        let newParams = this.props.queryParams;
        if (value === 'free' && checked === true) {
            newParams = modifyParamsWithDomesticFlatShipping(this.props.queryParams, 0);
        } else if (value === 'flat' && checked === true) {
            newParams = modifyParamsWithDomesticFlatShipping(this.props.queryParams, true);
        } else if (checked === false) {
            newParams = modifyParamsRemoveDomesticFlatShipping(this.props.queryParams);
        }
        this.navigateWithParams(newParams);
    };

    facetOptionChecked = (facetId, value, checked) => {
        let newParams = modifyParamsWhenChecked(this.props.queryParams, facetId, value, checked);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    facetOptionExcluded = (facetId, optionId, excluded) => {
        this.props.setSearchExclusion(facetId, optionId, excluded);
    };

    navigateWithParams = params => {
        let newParams = params;
        if (params.page === 1) {
            newParams = modifyParamsRemovePage(newParams);
        }
        this.props.navigate(getSearchUrlWithParams(newParams));
    };

    rangeEntered = (facetId, min, max) => {
        let newParams = modifyParamsWithRange(this.props.queryParams, facetId, min, max);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    removeCountryAndDistanceSelected = () => {
        let newParams = modifyParamsRemoveCountryAndDistance(this.props.queryParams);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    replaceCountrySelected = countryId => {
        let newParams = modifyParamsReplaceCountry(this.props.queryParams, countryId);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    removeFacetById = facetId => {
        let newParams = modifyParamsRemoveFacet(this.props.queryParams, facetId);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    removePublishedDate = () => {
        let newParams = modifyParamsRemovePublishDate(this.props.queryParams);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    sortSelected = sortOption => {
        let newParams = modifyParamsWithSort(this.props.queryParams, sortOption);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    submitSaveSearch = () => {
        const ssParams = getSaveSearchParams(this.props.queryParams);
        const sendSearchAlert = true;
        this.props.submitSaveSearch(ssParams, sendSearchAlert);
    };

    taxonomyAllSelected = (facetId, categoryId) => {
        let newParams = modifyParamsWithTaxonomyAll(this.props.queryParams, facetId, categoryId);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    taxonomyParentSelected = (parentId, categoryId, facetId) => {
        let newParams = modifyParamsWithTaxonomyParent(this.props.queryParams, facetId, categoryId, parentId);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    taxonomySelected = (facetId, id) => {
        let newParams = modifyParamsWithTaxonomySelected(this.props.queryParams, facetId, id, this.props.rawFacets);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams);
    };

    trackItemClick = (itemId, position) => {
        this.props.trackClickForAlgolia(itemId, position);
    };

    render() {
        const {
            archivedSearch,
            baseUrl,
            facets,
            houses,
            isLoading,
            items,
            page,
            pageName,
            pageSize,
            pathname,
            promotedItems,
            routeIsLoading,
            queryParams,
            saveSearchCheckState,
            saveSearchState,
            searchFailed,
            searchQuery,
            searchSubmitted,
            sortOptions,
            totalFound,
            totalLiveItems,
            totalPages,
            totalSoldItems,
            whiteLabel,
            width,
        } = this.props;

        return (
            <SearchPage
                archivedSearch={archivedSearch}
                baseUrl={baseUrl}
                changePageAndNavigate={this.changePageAndNavigate}
                changePageSizeAndNavigate={this.changePageSizeAndNavigate}
                changeSearchStatus={this.changeSearchStatus}
                clearAllParamsExceptCategories={this.clearAllParamsExceptCategories}
                deleteSavedSearch={this.deleteSavedSearch}
                distanceSelected={this.distanceSelected}
                facetOptionChecked={this.facetOptionChecked}
                facetOptionExcluded={this.facetOptionExcluded}
                facets={facets}
                houses={houses}
                isLoading={isLoading}
                items={items}
                liveItemsFound={totalLiveItems}
                page={page}
                pageName={pageName}
                pageSize={pageSize}
                pathname={pathname}
                promotedItems={promotedItems}
                queryParams={queryParams}
                rangeEntered={this.rangeEntered}
                removeCountryAndDistance={this.removeCountryAndDistanceSelected}
                removeFacetById={this.removeFacetById}
                removePublishedDate={this.removePublishedDate}
                replaceCountry={this.replaceCountrySelected}
                saveSearchCheckState={saveSearchCheckState}
                saveSearchState={saveSearchState}
                searchFailed={searchFailed}
                searchQuery={searchQuery}
                searchSubmitted={searchSubmitted || routeIsLoading}
                soldItemsFound={totalSoldItems}
                sortOptions={sortOptions}
                sortSelected={this.sortSelected}
                submitSaveSearch={this.submitSaveSearch}
                taxonomyAllSelected={this.taxonomyAllSelected}
                taxonomyParentSelected={this.taxonomyParentSelected}
                taxonomySelected={this.taxonomySelected}
                totalItemsFound={totalFound}
                totalPages={totalPages}
                trackItemClick={this.trackItemClick}
                whiteLabel={whiteLabel}
                width={width}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouteDataLoader(SearchPageContainer));
